import { useInView } from "../hooks/useInView";
import { CheckCircle2 } from "lucide-react";

const WHY_CHOOSE = [
  {
    title: "Expert Practitioners",
    desc: "Our team consists of highly experienced homeopathic doctors who are passionate about helping patients achieve optimal health. With years of specialized training, we offer the highest standard of care in homeopathy.",
  },
  {
    title: "Personalized Treatment Plans",
    desc: "We recognize that no two patients are the same. Through detailed consultations, we offer treatments tailored specifically to your individual symptoms and constitution.",
  },
  {
    title: "Safe and Natural Remedies",
    desc: "Homeopathic treatments at Silver Line are 100% natural, free from side effects, and can safely be used alongside conventional treatments. Our remedies are derived from natural substances such as plants, minerals, and other organic materials.",
  },
  {
    title: "Focus on Long-Term Wellness",
    desc: "While conventional medicine often focuses on symptom relief, homeopathy seeks to address the root cause of illness, leading to more sustainable and lasting results. Our treatments aim to strengthen the body's natural defenses and restore its balance.",
  },
  {
    title: "Holistic Healing",
    desc: "Homeopathy treats the whole person — mind, body, and spirit. Whether you're dealing with chronic physical conditions, mental health concerns like anxiety and depression, or simply want to boost your immune system, our treatments offer a complete healing experience.",
  },
];

function BulletItem({ item, index }) {
  const [ref, inView] = useInView(0.1);
  return (
    <div
      ref={ref}
      className="flex gap-3 items-start"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateX(0)" : "translateX(-20px)",
        transition: `opacity 0.5s ease ${index * 0.1}s, transform 0.5s ease ${index * 0.1}s`,
      }}
    >
      <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
      <div>
        <span className="font-semibold text-darktext text-sm">{item.title}: </span>
        <span className="text-gray-600 text-sm leading-relaxed">{item.desc}</span>
      </div>
    </div>
  );
}

export default function About() {
  const [textRef, textInView] = useInView(0.15);
  const [decRef, decInView] = useInView(0.1);

  return (
    <section id="about" className="py-20 bg-lightbg">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Text column */}
          <div ref={textRef}>
            <span
              className="inline-block text-xs font-semibold tracking-widest uppercase text-primary mb-3"
              style={{
                opacity: textInView ? 1 : 0,
                transition: "opacity 0.5s ease",
              }}
            >
              Our Story
            </span>
            <h2
              className="font-display text-3xl sm:text-4xl font-bold text-darktext mb-5"
              style={{
                opacity: textInView ? 1 : 0,
                transform: textInView ? "translateY(0)" : "translateY(20px)",
                transition: "opacity 0.5s ease 0.1s, transform 0.5s ease 0.1s",
              }}
            >
              About Us
            </h2>
            <p
              className="text-gray-600 leading-relaxed mb-8 text-sm sm:text-base"
              style={{
                opacity: textInView ? 1 : 0,
                transform: textInView ? "translateY(0)" : "translateY(20px)",
                transition: "opacity 0.5s ease 0.2s, transform 0.5s ease 0.2s",
              }}
            >
              Prepare to embark on a holistic healing journey like no other at "SilverLine Homeo
              Care", a renowned homeopathy clinic, nestled amidst the serene landscapes of Dehu
              Road, India. Established in the year 2012 (previously named as Aleena Homeopathy
              Clinic and location changed in 2024), this sanctuary of natural medicine welcomes
              individuals seeking a unique healing experience. Rooted in time-tested principles and
              profound expertise, SilverLine Homeo Care's experienced practitioners craft
              personalized treatment plans that address the very root causes of illness, leading you
              onto a path of optimal health and vitality.
            </p>

            <h3
              className="font-display font-bold text-darktext text-xl mb-5"
              style={{
                opacity: textInView ? 1 : 0,
                transition: "opacity 0.5s ease 0.3s",
              }}
            >
              Why Choose Silver Line Homeo Care?
            </h3>

            <div className="flex flex-col gap-4 mb-8">
              {WHY_CHOOSE.map((item, i) => (
                <BulletItem key={item.title} item={item} index={i} />
              ))}
            </div>

            <div
              style={{
                opacity: textInView ? 1 : 0,
                transform: textInView ? "translateY(0)" : "translateY(15px)",
                transition: "opacity 0.5s ease 0.6s, transform 0.5s ease 0.6s",
              }}
            >
              <button
                onClick={() => {
                  const el = document.getElementById("contact");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-flex items-center gap-2 bg-primary hover:bg-darktext text-white font-semibold px-7 py-3.5 rounded-full transition-all duration-300 hover:scale-105 shadow-md"
              >
                Get in Touch
              </button>
            </div>
          </div>

          {/* Decorative right column */}
          <div
            ref={decRef}
            className="relative hidden lg:flex items-center justify-center"
            style={{
              opacity: decInView ? 1 : 0,
              transform: decInView ? "scale(1)" : "scale(0.92)",
              transition: "opacity 0.7s ease, transform 0.7s ease",
            }}
          >
            <div className="w-full max-w-sm aspect-square relative">
              {/* Background circle */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/10 to-accent/20" />

              {/* Large leaf SVG */}
              <svg
                viewBox="0 0 400 400"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full h-full"
              >
                <circle cx="200" cy="200" r="180" fill="#f0faf4" />
                <path
                  d="M200 60 Q300 120 280 220 Q260 300 200 340 Q140 300 120 220 Q100 120 200 60Z"
                  fill="#2d8a4e"
                  opacity="0.15"
                />
                <path
                  d="M200 80 Q285 130 265 215 Q250 285 200 320 Q150 285 135 215 Q115 130 200 80Z"
                  fill="#2d8a4e"
                  opacity="0.2"
                />
                <path
                  d="M200 100 Q270 145 252 210 Q237 268 200 300 Q163 268 148 210 Q130 145 200 100Z"
                  fill="#2d8a4e"
                  opacity="0.35"
                />
                <line x1="200" y1="100" x2="200" y2="300" stroke="#66cc66" strokeWidth="2" strokeDasharray="6 4" opacity="0.6" />
                <line x1="200" y1="180" x2="260" y2="150" stroke="#66cc66" strokeWidth="1.5" opacity="0.5" />
                <line x1="200" y1="200" x2="148" y2="165" stroke="#66cc66" strokeWidth="1.5" opacity="0.5" />
                <line x1="200" y1="225" x2="255" y2="208" stroke="#66cc66" strokeWidth="1.5" opacity="0.5" />
                <circle cx="200" cy="200" r="30" fill="#2d8a4e" opacity="0.9" />
                <text x="200" y="205" textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="11" fontFamily="sans-serif" fontWeight="600">Since 2012</text>
              </svg>

              {/* Floating badges */}
              <div className="absolute top-6 right-0 bg-white rounded-xl shadow-lg px-3 py-2 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                <span className="text-xs font-semibold text-darktext">100% Natural</span>
              </div>
              <div className="absolute bottom-8 left-0 bg-white rounded-xl shadow-lg px-3 py-2 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-xs font-semibold text-darktext">Holistic Care</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
